/**
 * 🎓 v7.40 (per Javi, proposal §3 A + D): the ONE place that decides
 * whether this device - and, when a Google account is signed in, that
 * account - has played before. Three things read it:
 *
 *   - the title's welcome card ("Hey, welcome!"), shown to a first-timer;
 *   - the title's calm first-timer doors (Decision 4);
 *   - the run's `firstRun` stamp (useRun.startNewRun), the switch the
 *     first-run rails key off (the ⏩ button now, more in v7.41).
 *
 * Pure on purpose: storage comes in as an argument, so node tests drive it
 * with a Map. Read BY VALUE - the title's loaders write empty defaults (an
 * empty ledger, a zero unlock) that must never count as play.
 *
 * What is NOT earlier play: the practice (mode 'tutorial', its done flag),
 * the codex sightings and the Stardust the practice pays, the Daily
 * Silhouette, the language and audio switches. A player who has only done
 * those is still new to real runs.
 */
import { readFlag } from '~/utils/flagStore'
import { TUTORIAL_STARDUST } from '../../shared/economy'
import type { OnboardingPreference } from '~/composables/useOnboarding'

/** The device's own first-steps record (JSON, see FirstSteps). A wipe takes
 * it (app/utils/progressEpoch.ts PROGRESS_KEYS): a wiped device starts over. */
export const FIRST_STEPS_KEY = 'elemental-dispatch-first-steps-v1'
/** useState key of the signed-in account's cloud answer (null = none). */
export const FIRST_STEPS_CLOUD_STATE = 'first-steps-cloud'

type Store = Pick<Storage, 'getItem'> | null | undefined
type WritableStore = Pick<Storage, 'getItem' | 'setItem'> | null | undefined

export interface FirstSteps {
  /** The title found this device new (no earlier play, no practice done):
   * it opens calm until the first run ends (Decision 4). Never unset. */
  newcomer?: boolean
  /** A first run started here ('open'), then location 1 was cleared on
   * one ('done'). 'open' carries the rails to a restarted run. */
  firstRun?: 'open' | 'done'
  /** "You have 700 Stardust to spend!" was seen (the Shop was opened). */
  shopNote?: boolean
}

export function readFirstSteps(store: Store): FirstSteps {
  try {
    const raw = JSON.parse(store?.getItem(FIRST_STEPS_KEY) ?? 'null') as Partial<FirstSteps> | null
    if (!raw || typeof raw !== 'object') return {}
    const steps: FirstSteps = {}
    if (raw.newcomer === true) steps.newcomer = true
    if (raw.firstRun === 'open' || raw.firstRun === 'done') steps.firstRun = raw.firstRun
    if (raw.shopNote === true) steps.shopNote = true
    return steps
  } catch { return {} }
}

/** Merges `patch` into the stored record; returns the result. A 'done'
 * first run never goes back to 'open'. */
export function writeFirstSteps(store: WritableStore, patch: FirstSteps): FirstSteps {
  const current = readFirstSteps(store)
  const next: FirstSteps = { ...current, ...patch }
  if (current.firstRun === 'done') next.firstRun = 'done'
  try { store?.setItem(FIRST_STEPS_KEY, JSON.stringify(next)) } catch { /* private mode: this page load only */ }
  return next
}

/** What this device's storage says (numbers, never the raw records). */
export interface LocalPlay {
  /** The practice was finished once (or the old tips ran to the end). */
  tutorialDone: boolean
  /** A real run is saved or stashed (the campaign slot, a Puzzle Game or
   * practice-replay stash) - the practice itself is not one. */
  savedRun: boolean
  /** The practice is in progress in the campaign slot. */
  practiceSaved: boolean
  /** This week's (or any) Weekly Expedition is saved. */
  weeklyRun: boolean
  ascensionUnlocked: number
  ascensionCleared: number
  /** The lifetime ledger: runs started, and runs ended (won, lost or
   * abandoned), across every lane. */
  runsStarted: number
  runsEnded: number
  /** Puzzle Game levels with a best. */
  puzzleBests: number
  /** 🎓 v7.40 (review): the device's Stardust - the most its counters or
   * the wallet's cached balance show - and whether any was ever spent. The
   * same yardstick as the account's wallet (cloudSigns): only past what the
   * practice pays. */
  stardust: number
  stardustSpent: number
  /** Creatures owned for good (bought or pulled - never the practice's). */
  ownedCreatures: number
  /** Achievements banked. */
  achievements: number
}

/** The signed-in account's cloud copy, read by useFirstSteps (useOnboarding.ts).
 * Documents from before the current progress epoch are void (read as empty). */
export interface CloudPlay {
  ascensionUnlocked: number
  ascensionCleared: number
  puzzleBests: number
  /** A run document exists: a live run, or the tombstone of an ended one. */
  run: boolean
  /** The account's Stardust balance. */
  walletBalance: number
}

const RUN_KEY = 'elemental-dispatch-run-v6'
const WEEKLY_KEY = 'elemental-dispatch-daily-v6'
const STASH_KEYS = ['elemental-dispatch-run-stash-v1', 'elemental-dispatch-practice-stash-v1']
const TUTORIAL_DONE = 'elemental-dispatch-tutorial-done'
const LEDGER_KEY = 'elemental-dispatch-lifetime-v1'
// useStardust.ts: the pre-ledger counters and the server wallet's cache.
const STARDUST_KEY = 'elemental-dispatch-stardust'
const STARDUST_EARNED_KEY = 'elemental-dispatch-stardust-earned'
const STARDUST_SPENT_KEY = 'elemental-dispatch-stardust-spent'
const WALLET_CACHE_KEY = 'elemental-dispatch-wallet'
const OWNED_KEY = 'elemental-dispatch-owned-creatures'
const ACHIEVEMENTS_KEY = 'elemental-dispatch-achievements'

function json(store: Store, key: string): unknown {
  try { return JSON.parse(store?.getItem(key) ?? 'null') } catch { return null }
}
const count = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0)

export function readLocalPlay(store: Store): LocalPlay {
  const get = (key: string): string | null => { try { return store?.getItem(key) ?? null } catch { return null } }
  const slot = json(store, RUN_KEY) as { mode?: unknown } | null
  const slotRun = !!slot && typeof slot === 'object'
  const practiceSaved = slotRun && slot.mode === 'tutorial'
  const stashed = STASH_KEYS.some((key) => {
    const raw = get(key)
    return !!raw && raw !== 'null'
  })
  const cleared = json(store, 'elemental-dispatch-ascension-cleared')
  const ledger = json(store, LEDGER_KEY) as { runs?: Record<string, Record<string, unknown> | null> } | null
  let runsStarted = 0
  let runsEnded = 0
  if (ledger?.runs && typeof ledger.runs === 'object') {
    for (const tally of Object.values(ledger.runs)) {
      if (!tally || typeof tally !== 'object') continue
      runsStarted += count(tally.started)
      runsEnded += count(tally.won) + count(tally.lost) + count(tally.abandoned)
    }
  }
  const puzzle = json(store, 'elemental-dispatch-puzzle-v1')
  const weekly = get(WEEKLY_KEY)
  const earned = get(STARDUST_EARNED_KEY)
  const spent = count(Number(get(STARDUST_SPENT_KEY)))
  const legacy = earned === null ? count(Number(get(STARDUST_KEY))) : Math.max(0, count(Number(earned)) - spent)
  const wallet = json(store, WALLET_CACHE_KEY) as { balance?: unknown } | null
  const owned = json(store, OWNED_KEY)
  const achievements = json(store, ACHIEVEMENTS_KEY)
  return {
    tutorialDone: get(TUTORIAL_DONE) === '1' || readFlag(TUTORIAL_DONE) === '1',
    savedRun: (slotRun && !practiceSaved) || stashed,
    practiceSaved,
    weeklyRun: !!weekly && weekly !== 'null',
    ascensionUnlocked: count(Number(get('elemental-dispatch-ascension-unlocked'))),
    ascensionCleared: Array.isArray(cleared) ? cleared.length : 0,
    runsStarted,
    runsEnded,
    puzzleBests: puzzle && typeof puzzle === 'object' && !Array.isArray(puzzle) ? Object.keys(puzzle).length : 0,
    stardust: Math.max(legacy, count(wallet?.balance)),
    stardustSpent: spent,
    // 'legacy' is the achievement's own award (useAchievements' ownedCount).
    ownedCreatures: Array.isArray(owned) ? owned.filter(id => typeof id === 'string' && id !== 'legacy').length : 0,
    achievements: Array.isArray(achievements) ? achievements.filter(id => typeof id === 'string').length : 0,
  }
}

export type PlaySign =
  | 'ascension' | 'run-ended' | 'run-started' | 'run-saved' | 'weekly' | 'puzzle'
  | 'stardust' | 'owned' | 'achievements'
  | 'cloud-ascension' | 'cloud-puzzle' | 'cloud-run' | 'cloud-wallet'

/** The account's own signs of play. Its wallet counts only past what the
 * practice pays - an account that did the practice on another device is
 * still new to real runs. */
export function cloudSigns(cloud: CloudPlay | null | undefined): PlaySign[] {
  if (!cloud) return []
  const signs: PlaySign[] = []
  if (cloud.ascensionUnlocked > 0 || cloud.ascensionCleared > 0) signs.push('cloud-ascension')
  if (cloud.puzzleBests > 0) signs.push('cloud-puzzle')
  if (cloud.run) signs.push('cloud-run')
  if (cloud.walletBalance > TUTORIAL_STARDUST) signs.push('cloud-wallet')
  return signs
}

/** Every sign that this device (or the signed-in account) played a real run,
 * a Weekly Expedition, the Puzzle Game or an ascension BEFORE now. Empty =
 * a first-timer. The practice never counts. */
export function earlierPlay(local: LocalPlay, cloud: CloudPlay | null = null): PlaySign[] {
  const signs: PlaySign[] = []
  if (local.ascensionUnlocked > 0 || local.ascensionCleared > 0) signs.push('ascension')
  if (local.runsEnded > 0) signs.push('run-ended')
  if (local.runsStarted > 0) signs.push('run-started')
  if (local.savedRun) signs.push('run-saved')
  if (local.weeklyRun) signs.push('weekly')
  if (local.puzzleBests > 0) signs.push('puzzle')
  // 🎓 v7.40 (review): a device from before the ledger (v5.64) shows its
  // play in the Shop and the shelf - the account's yardstick for Stardust.
  if (local.stardust > TUTORIAL_STARDUST || local.stardustSpent > 0) signs.push('stardust')
  if (local.ownedCreatures > 0) signs.push('owned')
  if (local.achievements > 0) signs.push('achievements')
  return [...signs, ...cloudSigns(cloud)]
}

/** 🎓 v7.40 (per Javi, D3): the welcome card is for a first-timer - no
 * finished practice, no earlier play, and no "I've played before" (or a
 * skipped practice) on this device. */
export function welcomeEligible(local: LocalPlay, cloud: CloudPlay | null, preference: OnboardingPreference): boolean {
  if (local.tutorialDone || preference === 'dismissed' || preference === 'complete') return false
  return earlierPlay(local, cloud).length === 0
}

/**
 * 🎓 v7.40 (per Javi, D4): is the title calm right now? A device marked new
 * (FirstSteps.newcomer - or, when the account could not be heard, new for
 * this page load only) until its first run has ended; a device that is new
 * on its own evidence waits calm until decided (doors may appear once the
 * account answers, never disappear). An ascension or the account's own play
 * ends it at once. Only the doors, the What's New fold, the side column's
 * extras and the daily pop read it - nothing is hidden past the first run.
 */
export function titleCalm(o: {
  veteran: boolean
  runEnded: boolean
  newcomer: boolean
  provisional: boolean
  decided: boolean
  newOnDevice: boolean
}): boolean {
  if (o.veteran || o.runEnded) return false
  if (o.newcomer || o.provisional) return true
  return !o.decided && o.newOnDevice
}

/** 🎓 v7.40 (per Javi, D4): the title's doors. `calm` = this device opened
 * new (FirstSteps.newcomer) and nothing since says otherwise; its doors open
 * as the player gets there. Everyone else sees every door, as before. */
export function titleDoors(o: { calm: boolean; runEnded: boolean; practiceSettled: boolean }): { runDoors: boolean; profile: boolean } {
  if (!o.calm) return { runDoors: true, profile: true }
  // Weekly Expedition, Puzzle Game, Shop and Leaderboard: once the first
  // run has ended (won, lost or abandoned). Profile: once the practice is
  // finished or skipped (or a run has ended anyway).
  return { runDoors: o.runEnded, profile: o.runEnded || o.practiceSettled }
}

/** The run fields the first-run rule reads (a structural RunState slice). */
export interface FirstRunLike {
  firstRun?: boolean
  mode?: string
  gauntlet?: boolean
  puzzleLevel?: number
}

/** 🎓 v7.40: a run being replaced by a restart hands its first run on - the
 * rails stay until location 1 is cleared (v7.41's "Try again" reuses this). */
export function carriesFirstRun(prev: FirstRunLike | null | undefined): boolean {
  return prev?.firstRun === true && !prev.mode && !prev.gauntlet && typeof prev.puzzleLevel !== 'number'
}

/**
 * 🎓 v7.40 (per Javi, proposal §3 D): does a run starting NOW open as a
 * first run? Only a campaign run at Ascension 0 - never the Weekly
 * Expedition, the Gauntlet, a Puzzle Game session or the practice (those
 * never come through here with `campaign` set) - and only:
 *   - while this device has not cleared location 1 on a first run, and
 *   - as a restart of a first run (the run it replaces, or this device's
 *     open first run after a defeat or an abandon), or on a device - and
 *     signed-in account - with no sign of earlier play.
 */
export function startsFirstRun(
  start: { campaign: boolean; ascension: number },
  prev: FirstRunLike | null | undefined,
  steps: FirstSteps,
  local: LocalPlay,
  cloud: CloudPlay | null = null,
): boolean {
  if (!start.campaign || start.ascension !== 0) return false
  if (steps.firstRun === 'done') return false
  if (carriesFirstRun(prev) || steps.firstRun === 'open') return true
  return earlierPlay(local, cloud).length === 0
}
